import React, { useEffect, useMemo, useState } from 'react';
import { io } from 'socket.io-client';
import { enqueueSnackbar } from 'notistack';

import Modal from '../../../../components/modules/Modal';
import { ContainedButton } from '../../../../components/modules/Buttons';
import { postRunArchiveBackfill } from '../../../../api';
import { useAppSelector } from '../../../../store/hooks';
import styles from './SpecialOperationsModal.module.scss';

type TSpecialOperationsModal = {
  open: boolean;
  onClose: () => void;
};

type TAffectedVideofeed = {
  id: number;
  title: string;
  type: string;
  pre_delete_status: string | null;
  deleted_at: string | null;
  organization_id: number | null;
  organization_name: string | null;
  folder_id: number | null;
  folder_name: string | null;
};

type TArchiveBackfillResponse = {
  success: boolean;
  total: number;
  documents: TAffectedVideofeed[];
  scannedArchivedFeeds: number;
  processedChunks: number;
  processedKeys: number;
  durationMs: number;
};

type TArchiveBackfillSocketMessage = {
  type: string;
  event: 'started' | 'progress' | 'completed' | 'failed';
  message: string;
  data?: string;
};

const ARCHIVE_BACKFILL_SOCKET_EVENT = 'archive-backfill';

const mergeVideofeeds = (current: TAffectedVideofeed[], incoming: TAffectedVideofeed[]) => {
  const byId = new Map<number, TAffectedVideofeed>();
  [...current, ...incoming].forEach((item) => {
    if (!item?.id) return;
    byId.set(Number(item.id), item);
  });
  return [...byId.values()].sort((a, b) => b.id - a.id);
};

const getErrorMessage = (error: any, fallback: string) => String(error?.response?.data?.message || error?.message || fallback);

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(
    2,
    '0',
  )} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const SpecialOperationsModal: React.FC<TSpecialOperationsModal> = ({ open, onClose }) => {
  const { currentUser } = useAppSelector((state) => state.user);
  const [runLoading, setRunLoading] = useState(false);
  const [affectedVideofeeds, setAffectedVideofeeds] = useState<TAffectedVideofeed[]>([]);
  const [search, setSearch] = useState('');
  const [statusText, setStatusText] = useState('');
  const [resultStats, setResultStats] = useState<{
    total: number;
    scannedArchivedFeeds: number;
    processedChunks: number;
    processedKeys: number;
    durationMs: number;
  } | null>(null);
  const [hasRunOnce, setHasRunOnce] = useState(false);

  useEffect(() => {
    if (!open) {
      setRunLoading(false);
      setAffectedVideofeeds([]);
      setSearch('');
      setStatusText('');
      setResultStats(null);
      setHasRunOnce(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !currentUser?.id) return;

    const socket = io(`${process.env.REACT_APP_SOCKET_URL}`, {
      query: { userId: currentUser.id },
    });

    const onArchiveBackfillEvent = (rawMessage: string) => {
      try {
        const message = JSON.parse(rawMessage) as TArchiveBackfillSocketMessage;
        if (message?.message) {
          setStatusText(message.message);
        }

        const payload = message?.data ? JSON.parse(message.data) : null;
        if (Array.isArray(payload?.documents) && payload.documents.length) {
          setAffectedVideofeeds((prev) => mergeVideofeeds(prev, payload.documents));
        }

        if (message?.event === 'completed') {
          setResultStats({
            total: Number(payload?.total || 0),
            scannedArchivedFeeds: Number(payload?.scannedArchivedFeeds || 0),
            processedChunks: Number(payload?.processedChunks || 0),
            processedKeys: Number(payload?.processedKeys || 0),
            durationMs: Number(payload?.durationMs || 0),
          });
        }
      } catch {
        // ignore malformed socket payloads
      }
    };

    socket.on(ARCHIVE_BACKFILL_SOCKET_EVENT, onArchiveBackfillEvent);

    return () => {
      socket.off(ARCHIVE_BACKFILL_SOCKET_EVENT, onArchiveBackfillEvent);
      socket.disconnect();
    };
  }, [open, currentUser?.id]);

  const runArchiveBackfill = async () => {
    if (runLoading) return;

    try {
      setRunLoading(true);
      setHasRunOnce(true);
      setAffectedVideofeeds([]);
      setResultStats(null);
      setSearch('');
      setStatusText('Archive backfill requested. Waiting for server response...');

      const { data } = await postRunArchiveBackfill();
      const response = data as TArchiveBackfillResponse;
      const documents = Array.isArray(response?.documents) ? response.documents : [];

      setAffectedVideofeeds((prev) => mergeVideofeeds(prev, documents));
      setResultStats({
        total: Number(response?.total || documents.length || 0),
        scannedArchivedFeeds: Number(response?.scannedArchivedFeeds || 0),
        processedChunks: Number(response?.processedChunks || 0),
        processedKeys: Number(response?.processedKeys || 0),
        durationMs: Number(response?.durationMs || 0),
      });
      setStatusText(
        documents.length
          ? `Archive backfill completed. Delete markers were placed for ${documents.length} videofeed(s).`
          : 'Archive backfill completed. No new delete markers were required.',
      );
      enqueueSnackbar('Archive backfill completed.', {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    } catch (error: any) {
      setStatusText(getErrorMessage(error, 'Archive backfill failed.'));
      enqueueSnackbar(getErrorMessage(error, 'Failed to run archive backfill.'), {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    } finally {
      setRunLoading(false);
    }
  };

  const filteredVideofeeds = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return affectedVideofeeds;

    return affectedVideofeeds.filter((item) =>
      [item.id, item.title, item.organization_name, item.folder_name, item.type, item.pre_delete_status]
        .map((value) => String(value || '').toLowerCase())
        .some((value) => value.includes(needle)),
    );
  }, [affectedVideofeeds, search]);

  const showResults = hasRunOnce || runLoading || affectedVideofeeds.length > 0 || !!statusText;

  return (
    <Modal open={open} onClose={runLoading ? () => {} : onClose} loading={runLoading}>
      <div className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <div className={styles.headerCopy}>
            <div className={styles.eyebrow}>Super Admin Only</div>
            <div className={styles.modalSubtitle}>
              Run a global archive backfill and watch live videofeeds as new delete markers are actually placed.
            </div>
          </div>
          {runLoading && <div className={styles.headerBadge}>Running</div>}
        </div>

        <div className={styles.infoGrid}>
          <div className={styles.infoCard}>
            <div className={styles.cardTitle}>What this operation does</div>
            <div className={styles.copyBlock}>
              <p>
                <strong>Archive Backfill</strong> scans every videofeed in the database with <code>status = ARCHIVED</code> and attempts to
                place S3 delete markers on its archived assets.
              </p>
              <p>
                The request waits for completion. While it runs, this modal streams live results and the final response returns the full
                deduplicated videofeed list.
              </p>
            </div>
          </div>

          <div className={styles.warningCard}>
            <div className={styles.cardTitle}>Safety rules</div>
            <ul className={styles.bulletList}>
              <li>Works globally across all organisations.</li>
              <li>Does not delete videofeed rows from the database.</li>
              <li>Shared S3 keys used by active content are skipped.</li>
              <li>Only videofeeds with newly placed delete markers appear in the result list.</li>
            </ul>
          </div>
        </div>

        <div className={styles.actionsRow}>
          <div className={styles.runMeta}>
            This can take time on large servers. Keep this modal open if you want to watch live progress.
          </div>
          <ContainedButton kind="purple" onClick={runArchiveBackfill} disabled={runLoading} className={styles.runButton}>
            {runLoading ? 'Running Archive Backfill...' : 'Run Archive Backfill'}
          </ContainedButton>
        </div>

        {showResults && (
          <>
            <div className={styles.statusCard}>
              <div className={styles.statusHeader}>
                <div className={styles.statusTitle}>Execution Status</div>
                <div className={styles.statusPill}>{runLoading ? 'In Progress' : 'Completed'}</div>
              </div>
              <div className={styles.statusText}>{statusText || 'Waiting to start.'}</div>
              {resultStats && (
                <div className={styles.statsRow}>
                  <div className={styles.statItem}>
                    <span>Total affected</span>
                    <strong>{resultStats.total}</strong>
                  </div>
                  <div className={styles.statItem}>
                    <span>Archived scanned</span>
                    <strong>{resultStats.scannedArchivedFeeds}</strong>
                  </div>
                  <div className={styles.statItem}>
                    <span>Chunks</span>
                    <strong>{resultStats.processedChunks}</strong>
                  </div>
                  <div className={styles.statItem}>
                    <span>Marked keys</span>
                    <strong>{resultStats.processedKeys}</strong>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.resultsSection}>
              <div className={styles.previewHeader}>
                <div className={styles.previewTitle}>
                  Videofeeds with new delete markers
                  <span className={styles.count}>{affectedVideofeeds.length}</span>
                </div>
                <input
                  className={styles.searchInput}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by title, org, folder, id"
                />
              </div>

              <div className={styles.previewHint}>
                This list grows live during execution. The final HTTP response returns the same deduplicated videofeed list after the run
                finishes.
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Organisation</th>
                      <th>Folder</th>
                      <th>Deleted at</th>
                      <th>Pre-delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVideofeeds.length === 0 ? (
                      <tr>
                        <td colSpan={6} className={styles.emptyCell}>
                          {runLoading
                            ? 'Waiting for live results...'
                            : affectedVideofeeds.length === 0
                              ? 'No videofeeds received new delete markers in this run'
                              : 'No videofeeds match your search'}
                        </td>
                      </tr>
                    ) : (
                      filteredVideofeeds.map((item) => (
                        <tr key={item.id}>
                          <td className={styles.idCell}>{item.id}</td>
                          <td className={styles.titleCell}>
                            <div>{item.title || 'Untitled'}</div>
                            <span className={styles.typeText}>{item.type || '—'}</span>
                          </td>
                          <td>{item.organization_name || '—'}</td>
                          <td>{item.folder_name || '—'}</td>
                          <td>{formatDate(item.deleted_at)}</td>
                          <td>{item.pre_delete_status || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <div className={styles.footerActions}>
          <ContainedButton kind="purple" onClick={onClose} disabled={runLoading}>
            Close
          </ContainedButton>
        </div>
      </div>
    </Modal>
  );
};

export default SpecialOperationsModal;
